import { PharmacistLayout } from "@/components/layout/pharmacist-layout";
import { useState, useEffect, useCallback } from "react";
import { GraduationCap, ChevronDown, ChevronUp, Loader2 } from "lucide-react";

type Status = "developing" | "achieving" | "achieved";

const STATUS_CONFIG: Record<Status, { label: string; color: string }> = {
  developing: { label: "Developing", color: "bg-slate-100 text-slate-600 border-slate-200" },
  achieving: { label: "Achieving", color: "bg-blue-50 text-blue-700 border-blue-200" },
  achieved: { label: "Achieved", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

interface Competency {
  key: string;
  label: string;
}

interface Domain {
  key: string;
  title: string;
  competencies: Competency[];
}

interface Pillar {
  key: string;
  title: string;
  color: string;
  domains: Domain[];
}

const APPF_PILLARS: Pillar[] = [
  {
    key: "epp",
    title: "Expert Professional Practice",
    color: "from-violet-700 to-violet-900",
    domains: [
      {
        key: "epp1",
        title: "Clinical Expertise",
        competencies: [
          { key: "epp1a", label: "Applies advanced clinical pharmacology knowledge to patient care" },
          { key: "epp1b", label: "Independently manages complex pharmaceutical care for patients with multimorbidity" },
          { key: "epp1c", label: "Evaluates evidence critically to inform prescribing and medicines optimisation decisions" },
          { key: "epp1d", label: "Leads medicines reconciliation across care settings" },
        ],
      },
      {
        key: "epp2",
        title: "Medicines Optimisation",
        competencies: [
          { key: "epp2a", label: "Leads therapeutic drug monitoring and dose optimisation programmes" },
          { key: "epp2b", label: "Provides specialist medicines advice to clinicians and MDT" },
          { key: "epp2c", label: "Implements and evaluates medicines adherence strategies" },
        ],
      },
      {
        key: "epp3",
        title: "Non-Medical Prescribing",
        competencies: [
          { key: "epp3a", label: "Practises as an independent or supplementary prescriber within their competence" },
          { key: "epp3b", label: "Maintains prescribing competency through the RPS Competency Framework" },
        ],
      },
    ],
  },
  {
    key: "cwr",
    title: "Collaborative Working Relationships",
    color: "from-blue-700 to-blue-900",
    domains: [
      {
        key: "cwr1",
        title: "MDT Collaboration",
        competencies: [
          { key: "cwr1a", label: "Acts as an integral, expert member of multidisciplinary rheumatology teams" },
          { key: "cwr1b", label: "Advocates for medicines safety and patient-centred care within MDT discussions" },
          { key: "cwr1c", label: "Develops productive relationships with specialist nurses, consultants, and GPs" },
        ],
      },
      {
        key: "cwr2",
        title: "Service Development",
        competencies: [
          { key: "cwr2a", label: "Contributes to the design and evaluation of pharmacy-led clinical services" },
          { key: "cwr2b", label: "Engages in quality improvement projects relating to medicines use" },
        ],
      },
    ],
  },
  {
    key: "lead",
    title: "Leadership",
    color: "from-amber-600 to-amber-800",
    domains: [
      {
        key: "lead1",
        title: "Strategic Influence",
        competencies: [
          { key: "lead1a", label: "Influences medicines policy and formulary decisions at departmental or organisational level" },
          { key: "lead1b", label: "Leads or contributes to pharmacy service governance and safety culture" },
          { key: "lead1c", label: "Mentors junior pharmacists and pharmacy technicians" },
        ],
      },
      {
        key: "lead2",
        title: "Innovation & Improvement",
        competencies: [
          { key: "lead2a", label: "Designs, implements, and evaluates service improvement initiatives" },
          { key: "lead2b", label: "Contributes to research, audit, or clinical governance activities" },
        ],
      },
    ],
  },
  {
    key: "etd",
    title: "Education, Training & Development",
    color: "from-emerald-700 to-emerald-900",
    domains: [
      {
        key: "etd1",
        title: "Teaching & Supervision",
        competencies: [
          { key: "etd1a", label: "Teaches pharmacy students, trainees, and healthcare colleagues" },
          { key: "etd1b", label: "Acts as a designated supervisor or educational supervisor for pharmacists in training" },
          { key: "etd1c", label: "Provides clinical teaching within the rheumatology setting" },
        ],
      },
      {
        key: "etd2",
        title: "Own Development",
        competencies: [
          { key: "etd2a", label: "Maintains a portfolio of CPD that reflects the full GPhC cycle" },
          { key: "etd2b", label: "Works towards or has achieved postgraduate clinical qualifications (e.g. MSc, PGCert)" },
          { key: "etd2c", label: "Engages with RPS Faculty credentialing processes" },
        ],
      },
    ],
  },
];

interface PortfolioEntry {
  frameworkKey: string;
  domainKey: string;
  competencyKey: string;
  status: Status;
  evidenceNotes: string | null;
}

export default function RpsFacultyPortfolio() {
  const [entries, setEntries] = useState<Map<string, PortfolioEntry>>(new Map());
  const [saving, setSaving] = useState<string | null>(null);
  const [expandedPillar, setExpandedPillar] = useState<string | null>("epp");
  const [expandedDomain, setExpandedDomain] = useState<string | null>(null);
  const [editingNotes, setEditingNotes] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState("");

  const loadEntries = useCallback(async () => {
    const res = await fetch("/api/pharmacist/portfolio");
    if (!res.ok) return;
    const data: PortfolioEntry[] = await res.json();
    const map = new Map<string, PortfolioEntry>();
    for (const e of data) {
      if (e.frameworkKey === "appf") {
        map.set(e.competencyKey, e);
      }
    }
    setEntries(map);
  }, []);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const getStatus = (compKey: string): Status =>
    entries.get(compKey)?.status ?? "developing";

  const getNotes = (compKey: string): string =>
    entries.get(compKey)?.evidenceNotes ?? "";

  const upsert = async (pillarKey: string, domainKey: string, compKey: string, status: Status, evidenceNotes?: string) => {
    setSaving(compKey);
    try {
      const body: Record<string, string> = { status };
      if (evidenceNotes !== undefined) body.evidenceNotes = evidenceNotes;
      const res = await fetch(`/api/pharmacist/portfolio/appf/${domainKey}/${compKey}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const updated: PortfolioEntry = await res.json();
        setEntries((prev) => {
          const next = new Map(prev);
          next.set(compKey, updated);
          return next;
        });
      }
    } finally {
      setSaving(null);
    }
  };

  // Compute readiness per pillar
  const pillarReadiness = (pillar: Pillar) => {
    let total = 0;
    let achieved = 0;
    for (const d of pillar.domains) {
      for (const c of d.competencies) {
        total++;
        const s = getStatus(c.key);
        if (s === "achieved") achieved++;
      }
    }
    return { total, achieved, pct: total > 0 ? Math.round((achieved / total) * 100) : 0 };
  };

  const allReadiness = () => {
    let total = 0, achieved = 0;
    for (const p of APPF_PILLARS) {
      const r = pillarReadiness(p);
      total += r.total;
      achieved += r.achieved;
    }
    return { total, achieved, pct: total > 0 ? Math.round((achieved / total) * 100) : 0 };
  };

  const overall = allReadiness();

  return (
    <PharmacistLayout title="RPS Faculty Portfolio — APPF">
      <div className="space-y-6">

        {/* Header */}
        <div className="bg-gradient-to-br from-violet-900 to-violet-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <GraduationCap className="h-8 w-8 text-violet-300" />
            <div>
              <h2 className="text-xl font-bold">RPS Faculty Portfolio</h2>
              <p className="text-violet-300 text-sm">Advanced Pharmacy Practice Framework (APPF) · Four Pillars</p>
            </div>
          </div>
          <p className="text-violet-200 text-sm mb-4">
            Track your evidence against the RPS Advanced Pharmacy Practice Framework — the basis for RPS Credentialing and Faculty membership. Map evidence to each pillar and mark competencies as developing, achieving, or achieved.
          </p>
          <div className="bg-white/10 rounded-xl px-4 py-3">
            <div className="flex justify-between text-sm text-violet-200 mb-1">
              <span>Overall APPF readiness</span>
              <span>{overall.achieved}/{overall.total} competencies achieved</span>
            </div>
            <div className="h-2 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-violet-400 rounded-full transition-all" style={{ width: `${overall.pct}%` }} />
            </div>
            <p className="text-xs text-violet-300 mt-1">{overall.pct}% ready for RPS credentialing assessment</p>
          </div>
        </div>

        {/* Pillars */}
        {APPF_PILLARS.map((pillar) => {
          const r = pillarReadiness(pillar);
          const open = expandedPillar === pillar.key;
          return (
            <div key={pillar.key} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <button
                onClick={() => setExpandedPillar(open ? null : pillar.key)}
                className="w-full flex items-center gap-4 px-6 py-4 text-left hover:bg-slate-50 transition-colors"
              >
                <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${pillar.color} flex items-center justify-center shrink-0`}>
                  <GraduationCap className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-900">{pillar.title}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 bg-slate-100 rounded-full h-1.5 max-w-[200px]">
                      <div className="bg-violet-500 h-1.5 rounded-full transition-all" style={{ width: `${r.pct}%` }} />
                    </div>
                    <span className="text-xs text-slate-500">{r.achieved}/{r.total} achieved · {r.pct}%</span>
                  </div>
                </div>
                {open ? <ChevronUp className="h-5 w-5 text-slate-400 shrink-0" /> : <ChevronDown className="h-5 w-5 text-slate-400 shrink-0" />}
              </button>

              {open && (
                <div className="border-t border-slate-100">
                  {pillar.domains.map((domain) => {
                    const domainOpen = expandedDomain === domain.key;
                    return (
                      <div key={domain.key} className="border-b border-slate-100 last:border-0">
                        <button
                          onClick={() => setExpandedDomain(domainOpen ? null : domain.key)}
                          className="w-full flex items-center gap-3 px-6 py-3 text-left hover:bg-slate-50 transition-colors"
                        >
                          <span className="flex-1 text-sm font-semibold text-slate-800">{domain.title}</span>
                          <span className="text-xs text-slate-400">
                            {domain.competencies.filter((c) => getStatus(c.key) === "achieved").length}/{domain.competencies.length} achieved
                          </span>
                          {domainOpen ? <ChevronUp className="h-4 w-4 text-slate-300 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-300 shrink-0" />}
                        </button>

                        {domainOpen && (
                          <div className="px-6 pb-4 space-y-3">
                            {domain.competencies.map((comp) => {
                              const status = getStatus(comp.key);
                              const notes = getNotes(comp.key);
                              const isEditing = editingNotes === comp.key;
                              const isSaving = saving === comp.key;

                              return (
                                <div key={comp.key} className="bg-slate-50 rounded-xl p-4 space-y-3">
                                  <div className="flex items-start gap-3">
                                    <div className="flex-1">
                                      <p className="text-sm text-slate-800 leading-snug">{comp.label}</p>
                                    </div>
                                    <div className="flex gap-1.5 shrink-0 flex-wrap justify-end">
                                      {(["developing", "achieving", "achieved"] as Status[]).map((s) => (
                                        <button
                                          key={s}
                                          disabled={isSaving}
                                          onClick={() => upsert(pillar.key, domain.key, comp.key, s, notes || undefined)}
                                          className={`px-2.5 py-1 text-xs font-medium rounded-lg border transition-all ${
                                            status === s
                                              ? STATUS_CONFIG[s].color + " ring-1 ring-offset-1 ring-current"
                                              : "bg-white border-slate-200 text-slate-500 hover:bg-slate-100"
                                          }`}
                                        >
                                          {isSaving && status !== s ? <Loader2 className="h-3 w-3 animate-spin inline" /> : STATUS_CONFIG[s].label}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                  {isEditing ? (
                                    <div className="space-y-2">
                                      <textarea
                                        value={notesDraft}
                                        onChange={(e) => setNotesDraft(e.target.value)}
                                        rows={3}
                                        placeholder="Evidence notes — describe the evidence you have for this competency..."
                                        className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
                                      />
                                      <div className="flex gap-2">
                                        <button
                                          onClick={() => {
                                            upsert(pillar.key, domain.key, comp.key, status, notesDraft);
                                            setEditingNotes(null);
                                          }}
                                          className="px-3 py-1 bg-violet-700 hover:bg-violet-800 text-white text-xs font-medium rounded-lg transition-colors"
                                        >Save notes</button>
                                        <button
                                          onClick={() => setEditingNotes(null)}
                                          className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-medium rounded-lg transition-colors"
                                        >Cancel</button>
                                      </div>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => { setEditingNotes(comp.key); setNotesDraft(notes); }}
                                      className="text-xs text-violet-600 hover:text-violet-800 transition-colors"
                                    >
                                      {notes ? `📝 ${notes.slice(0, 80)}${notes.length > 80 ? "…" : ""}` : "+ Add evidence notes"}
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
          <strong>Attribution:</strong> Competency descriptors are based on the RPS Advanced Pharmacy Practice Framework (APPF) developed by the Royal Pharmaceutical Society. For RPS Credentialing and Faculty membership, evidence must be formally assessed by RPS Faculty assessors. This tracker is for personal planning only. See <a href="https://www.rpharms.com/development/rps-faculty" target="_blank" rel="noopener noreferrer" className="underline hover:text-violet-600">rpharms.com/development/rps-faculty</a>.
        </div>
      </div>
    </PharmacistLayout>
  );
}
